import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMatch } from "react-router-dom";

import Confirm from "components/atoms/alert/Confirm";
import useToast from "components/atoms/toast/useToast";

import {
    citationReference,
    deleteBulkResearch,
    deleteReferenceCitation,
    deleteReferenceCitationByUuid,
    deleteReferenceResearch,
    deleteResearch,
    deleteResearchAnalysis,
    deleteResearchMedia,
    getResearch,
    getResearchAnalysis,
    getResearchList,
    getResearchMedia,
    patchResearch,
    postReferenceResearch,
    postResearch,
    postResearchMedia,
    postResearchWithHtml,
} from "utils/request/research";

const useCurrentSpaceUuid = () => {
    const match = useMatch("/:space_uuid/*");
    return match?.params?.space_uuid ?? "";
};

export const useResearches = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["researches", space_uuid, params], (e) => getResearchList(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        keepPreviousData: true,
    });
};

export const useResearch = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery("research", (e) => getResearch(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useResearchDelete = (params) => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => deleteResearch(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "researches" });
            queryClient.invalidateQueries({ queryKey: "researchNameList" });
            queryClient.invalidateQueries({ queryKey: "bibliographies" });
            queryClient.invalidateQueries({ queryKey: "bibliography" });
        },
    });
};

export const useResearchBulkDelete = (params) => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => deleteBulkResearch(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "researches" });
            queryClient.invalidateQueries({ queryKey: "researchNameList" });
            queryClient.invalidateQueries({ queryKey: "bibliographies" });
            queryClient.invalidateQueries({ queryKey: "bibliography" });
        },
    });
};

export const useResearchCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => postResearch(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "researches" });
            queryClient.invalidateQueries({ queryKey: "researchNameList" });
        },
    });
};

export const useResearchUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => patchResearch(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "bibliography" });
            queryClient.invalidateQueries({ queryKey: "bibliographies" });
            queryClient.invalidateQueries({ queryKey: "research" });
            queryClient.invalidateQueries({ queryKey: "researches" });
            queryClient.invalidateQueries({ queryKey: "researchNameList" });
        },
    });
};

export const useResearchReferencesCreate = (body) => {
    const queryClient = useQueryClient();
    const { setToast } = useToast();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => postReferenceResearch(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "researchReferences" });
            queryClient.invalidateQueries({ queryKey: "researches" });
            queryClient.invalidateQueries({ queryKey: "research" });
            queryClient.invalidateQueries({ queryKey: "bibliography" });
            queryClient.invalidateQueries({ queryKey: "bibliographies" });
            setToast("적용되었습니다.", "info");
        },
    });
};

export const useReferenceCitationCreate = (body) => {
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((variables) => citationReference(space_uuid, variables), {
        onSuccess: (data, variables, context) => {},
    });
};

export const useReferenceCitationDelete = (body) => {
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((variables) => deleteReferenceCitation(space_uuid, variables), {
        onSuccess: (data, variables, context) => {},
    });
};

export const useReferenceCitationDeleteByUuid = (body) => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((variables) => deleteReferenceCitationByUuid(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useReferenceResearchDelete = (body) => {
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((variables) => deleteReferenceResearch(space_uuid, variables), {
        onSuccess: (data, variables, context) => {},
    });
};

export const useResearchMedia = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery("researchMedia", (e) => getResearchMedia(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useResearchMediaCreate = (body) => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((variables) => postResearchMedia(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "researchMedia" });
        },
    });
};

export const useResearchMediaBulkDelete = (body) => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((variables) => deleteResearchMedia(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "researchMedia" });
        },
    });
};

export const useResearchWithHtmlCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => postResearchWithHtml(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "researches" });
            queryClient.invalidateQueries({ queryKey: "researchNameList" });
            Confirm("info", "알림", "연구노트로 저장되었습니다. \n이동하여 확인하시겠습니까?", "이동", () => {
                window.open(`/${space_uuid}/research/${data.research_no}`, "_blank", "noopener noreferrer");
            });
        },
    });
};

export const useReferenceAnalysis = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["referenceAnalysis", space_uuid], (e) => getResearchAnalysis(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useReferenceAnalysisDelete = (body) => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => deleteResearchAnalysis(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "referenceAnalysis" });
            queryClient.invalidateQueries({ queryKey: "research" });
            queryClient.invalidateQueries({ queryKey: "researches" });
        },
    });
};
